import type { Classification } from '@/types'
import { cn } from '@/lib/utils'

interface ClassificationChipGridProps {
  classifications: Classification[]
  selectedId?: string | null
  onSelect: (classificationId: string) => void
  disabled?: boolean
}

/**
 * Big, touch-friendly classification picker.
 * Each chip is ≥48px tall — comfortable for receptionists on phones/tablets.
 */
export function ClassificationChipGrid({
  classifications,
  selectedId,
  onSelect,
  disabled,
}: ClassificationChipGridProps) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
      {classifications.map((c) => {
        const selected = selectedId === c.id
        return (
          <button
            key={c.id}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(c.id)}
            className={cn(
              'group flex min-h-[56px] items-center gap-3 rounded-lg border px-3 py-3 text-left transition',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'border-gold bg-paper-3 shadow-sm'
                : 'border-rule bg-paper-2/40 hover:border-gold-soft hover:bg-paper-2'
            )}
            aria-pressed={selected}
          >
            <span
              className="h-5 w-5 shrink-0 rounded-full border border-white/40 shadow-inner"
              style={{ backgroundColor: c.color }}
            />
            <span className="font-sans text-[14px] font-medium leading-tight text-ink">
              {c.displayName}
            </span>
          </button>
        )
      })}
    </div>
  )
}
